import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const WebDesigningCourse = () => {
  const testimonials = [
    {
      id: 1,
      name: "Shruti Gupta",
      review:
        "The Web Designing course at Nadezhda Venture exceeded my expectations. I learned so much, from responsive design to UI/UX principles, and now I'm confident in building beautiful websites. Highly recommend!",
      rating: 5,
      date: "2024-07-30",
      profileImg: "https://randomuser.me/api/portraits/women/65.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Rohan Kumar",
      review:
        "This course is a must for anyone aspiring to be a web designer. The instructors break down complex topics into easy-to-understand modules, and the practical projects are invaluable. Best web design training in Noida!",
      rating: 4,
      date: "2024-07-28",
      profileImg: "https://randomuser.me/api/portraits/men/48.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Disha Sharma",
      review:
        "I'm thrilled with the skills I gained. Learning Figma, Photoshop, and advanced CSS concepts has transformed my career. The portfolio-building guidance was fantastic.",
      rating: 5,
      date: "2024-07-25",
      profileImg: "https://randomuser.me/api/portraits/women/70.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Vijay Singh",
      review:
        "The practical sessions were the highlight. I built several functional websites from scratch, which gave me immense confidence. The placement team is also very supportive.",
      rating: 4,
      date: "2024-07-23",
      profileImg: "https://randomuser.me/api/portraits/men/80.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Sonia Devi",
      review:
        "From understanding client requirements to final deployment, every aspect of web design was covered thoroughly. The focus on responsive design and user experience made a huge difference.",
      rating: 5,
      date: "2024-07-20",
      profileImg: "https://randomuser.me/api/portraits/women/55.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];
   const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const faqContent = [
    {
      title: "What is Web Designing?",
      answer:
        "Web Designing involves creating the visual appearance and layout of websites. It focuses on user experience (UX) and user interface (UI) to make websites aesthetically pleasing, functional, and easy to navigate. This includes HTML, CSS, JavaScript, graphic design tools, and responsive design principles.",
    },
    {
      title: "What are the prerequisites for joining this course?",
      answer:
        "There are no specific prerequisites! This course is designed for beginners with no prior coding or design experience. All you need is a passion for creativity and a willingness to learn. We start from the absolute basics.",
    },
    {
      title: "What topics are covered in your Web Designing course?",
      answer:
        "Our comprehensive course covers HTML5, CSS3 (including Flexbox and Grid), responsive web design, JavaScript fundamentals, UI/UX principles, Adobe Photoshop, Figma, website wireframing and prototyping, front-end frameworks (like Bootstrap), and basic SEO for web designers. You'll also work on live projects.",
    },
    {
      title: "How is the training delivered (online/offline)?",
      answer:
        "We offer flexible training modes: intensive offline classroom sessions at our Noida center and interactive live online classes. Both modes feature hands-on projects, expert instructors, and a supportive learning environment.",
    },
    {
      title: "Will I get placement assistance after completing the course?",
      answer:
        "Yes, we provide dedicated placement assistance to our web design graduates. This includes portfolio building, resume preparation, mock interviews, and connecting you with hiring partners for roles like Web Designer, UI Designer, and Front-End Developer.",
    },
    {
      title: "What kind of projects will I work on during the training?",
      answer:
        "You will work on multiple practical projects, including designing and developing responsive websites for various industries (e-commerce, portfolio, corporate, blog). This will help you build a strong portfolio to showcase your skills.",
    },
    {
      title: "Is there a certification provided after the Web Designing course?",
      answer:
        "Yes, upon successful completion of the course and project work, you will receive a professional Web Designing certification from Appwars Technologies, recognized by the industry.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: Introduction to Web Design & UI/UX Principles",
      description: "Understanding web design fundamentals, client requirements, target audience, principles of good UI/UX, user-centered design, and design thinking.",
    },
    {
      title: "Module 2: HTML5 - Structuring Web Content",
      description: "Mastering HTML5 tags, semantic HTML, forms, tables, multimedia integration, and creating the basic structure of a webpage.",
    },
    {
      title: "Module 3: CSS3 - Styling Web Pages",
      description: "In-depth CSS selectors, properties, box model, typography, colors, backgrounds, gradients, transitions, animations, Flexbox, and CSS Grid for advanced layouts.",
    },
    {
      title: "Module 4: Responsive Web Design",
      description: "Techniques for creating websites that adapt to various screen sizes (desktops, tablets, mobiles) using media queries and mobile-first approach. Introduction to Bootstrap framework.",
    },
    {
      title: "Module 5: JavaScript Fundamentals for Web Designers",
      description: "Basic JavaScript syntax, variables, data types, operators, conditional statements, loops, functions, and DOM manipulation to add interactivity to web pages.",
    },
    {
      title: "Module 6: Graphic Design Tools (Photoshop & Figma)",
      description: "Practical training in Adobe Photoshop for image editing, optimization, and web layouts. Introduction to Figma for UI prototyping, wireframing, and collaborative design.",
    },
    {
      title: "Module 7: Website Planning, Wireframing & Prototyping",
      description: "Concepts of information architecture, creating sitemaps, wireframing tools and techniques, and developing interactive prototypes for user testing.",
    },
    {
      title: "Module 8: Web Hosting & Domain Management Basics",
      description: "Understanding domain names, web hosting types, cPanel basics, uploading files via FTP, and making your website live.",
    },
    {
      title: "Module 9: SEO Basics for Web Designers",
      description: "On-page SEO techniques: meta tags, image optimization, clean code, site speed, and structured data to make websites search engine friendly.",
    },
    {
      title: "Module 10: Capstone Project & Portfolio Development",
      description: "Design and develop a complete, professional, multi-page responsive website from scratch, incorporating all learned concepts. This project will be the centerpiece of your portfolio.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(getVisibleTestimonialsCount());

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(testimonials[(startIndex + i) % testimonials.length]);
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Web Designing Course in Noida | Master UI/UX, HTML, CSS, Figma</title>
        <meta
          name="description"
          content="Enroll in the best Web Designing course in Noida. Learn UI/UX design, HTML5, CSS3, JavaScript, Photoshop, Figma, and build stunning, responsive websites with placement support."
        />
        <meta
          name="keywords"
          content="Web Designing course Noida, UI UX design training, HTML CSS JavaScript, Figma training, Photoshop for web, Responsive Web Design, Web design certification, Front-end design course, Graphic design web, Website designing course"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://png.pngtree.com/thumb_back/fh260/back_pic/03/80/06/3757c30a762387b.jpg"
          alt="Web Designing Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-40 z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              <span className="text-orange-400">Web Designing</span> Course in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Unleash your creativity and master the art of building stunning, user-friendly websites with our expert-led web designing course.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-orange-700 hover:bg-orange-400 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
              <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("Web Designing Course")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Why Learn Web Design Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Learn Web Designing?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            In today's digital age, a strong online presence is crucial. Web designers are in high demand to create engaging and effective websites for businesses and individuals worldwide. Learning web design opens doors to a highly creative and rewarding career.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-pink-700 mb-3">High Demand & Growth</h3>
              <p className="text-gray-600">The digital landscape constantly needs skilled web designers.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-pink-700 mb-3">Creative Outlet</h3>
              <p className="text-gray-600">Combine art and technology to bring ideas to life on the web.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-pink-700 mb-3">Freelance Opportunities</h3>
              <p className="text-gray-600">Work independently with clients globally, setting your own hours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🎨', title: 'Master UI/UX Principles', desc: 'Learn to create intuitive and engaging user interfaces and experiences.' },
              { icon: '💻', title: 'Hands-on HTML, CSS, JavaScript', desc: 'Build responsive and interactive websites from scratch.' },
              { icon: '🖌️', title: 'Proficiency in Design Tools', desc: 'Gain expertise in industry-standard tools like Figma and Adobe Photoshop.' },
              { icon: '🎓', title: 'Industry-Recognized Certification', desc: 'Receive a professional certificate to boost your career prospects.' },
              { icon: '🤝', title: 'Dedicated Placement Assistance', desc: 'Comprehensive support for portfolio building, interviews, and job placements.' },
              { icon: '🌐', title: 'Build a Stunning Portfolio', desc: 'Develop multiple real-world projects to showcase your skills to employers.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-blue-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-blue-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Technologies You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our Web Designing course ensures you gain proficiency in all essential software and languages for modern web design.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'HTML5', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg' },
              { name: 'CSS3', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg' },
              { name: 'JavaScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
              { name: 'Figma', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg' },
              { name: 'Adobe Photoshop', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/photoshop/photoshop-plain.svg' },
              { name: 'Bootstrap', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bootstrap/bootstrap-original.svg' },
              { name: 'VS Code', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg' },
              { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A Web Designing certification from us will open doors to a variety of creative and in-demand roles in the digital industry.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '🖥️', title: 'Web Designer', desc: 'Create visually appealing and functional websites for various clients and industries.' },
              { icon: '💡', title: 'UI Designer', desc: 'Focus on the visual and interactive elements of user interfaces.' },
              { icon: '🚶‍♀️', title: 'UX Designer', desc: 'Improve user satisfaction by enhancing the usability, accessibility, and pleasure in user interaction.' },
              { icon: '✍️', title: 'Graphic Designer', desc: 'Develop visual concepts using computer software or by hand, to communicate ideas that inspire, inform, or captivate consumers.' },
              { icon: '🎨', title: 'Front-End Developer (Design Focus)', desc: 'Implement web designs using HTML, CSS, and JavaScript, focusing on the visual and interactive aspects.' },
              { icon: '📈', title: 'Freelance Web Designer', desc: 'Work independently, offering web design services to diverse clients globally.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-blue-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-gray-100 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our Web Designing course.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., Web Designing Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-pink-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-pink-900">
            What Our Web Design Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-pink-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-pink-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-pink-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

       {/* Conditionally render the popup */}
            {isPopupOpen && (
              <JoinNowFormPopup
                onClose={handleClosePopup}
                courseTitle={selectedCourseTitle}
              />
            )}
    </div>
  );
};

export default WebDesigningCourse;